/**
 * Shared "which video model will be used" resolution for the chat config cards
 * (ProjectSetupCard / GlobalConfigActionCard) and the Global Config
 * preferred-models section, so the surfaces cannot disagree.
 *
 * Feature tag: `project-setup-card` (concern `preferred-video-models`).
 */

export type VideoModelRowResolution =
  | { kind: 'preferred'; ids: string[] }
  | { kind: 'default'; id: string; runnable: boolean }
  | { kind: 'loading' }

/**
 * Which video model(s) a config surface should name.
 *
 * `effective_default_video_model` is always populated by the API (the first
 * selectable preferred model, else the Dreamwrite default); the catalog
 * `defaultModel` only covers an older API instance during a rolling deploy.
 * The preferred list is shown when it resolves — i.e. the effective default is
 * one of its entries. Otherwise (empty list, or every entry unavailable) the
 * Dreamwrite default is named. There is no "—" branch: before either source
 * loads the caller shows its loading text.
 *
 * `effectiveDefaultRunnable === false` means the API's effective default is the
 * user's enabled default My Video Model that cannot run (missing run URL or
 * durations). The Director refuses it rather than falling back (#3943), so the
 * row is flagged `runnable: false` and callers render a "needs repair" label
 * instead of presenting it as a working default.
 */
export function resolveVideoModelRow(
  preferred: readonly string[],
  effectiveDefault: string | null | undefined,
  catalogDefault: string | null,
  effectiveDefaultRunnable: boolean | undefined = true,
): VideoModelRowResolution {
  const runnable = effectiveDefaultRunnable !== false
  if (preferred.length > 0) {
    // A preference that resolves names itself; one where every entry is
    // unavailable names the default the API fell back to.
    return effectiveDefault && !preferred.includes(effectiveDefault)
      ? { kind: 'default', id: effectiveDefault, runnable }
      : { kind: 'preferred', ids: [...preferred] }
  }
  if (effectiveDefault) return { kind: 'default', id: effectiveDefault, runnable }
  return catalogDefault ? { kind: 'default', id: catalogDefault, runnable: true } : { kind: 'loading' }
}
